/* Admin-wide display timezone. Every date in the admin panel is rendered through
   these helpers so the whole panel switches together (default: Pacific/Vancouver). */
import { useEffect, useState } from 'react';

export const DEFAULT_TZ = 'America/Vancouver';
export const TIMEZONES = [
  { value: 'America/Vancouver', label: 'Pacific — Vancouver (PST/PDT)' },
  { value: 'America/Edmonton', label: 'Mountain — Edmonton/Calgary' },
  { value: 'America/Winnipeg', label: 'Central — Winnipeg' },
  { value: 'America/Toronto', label: 'Eastern — Toronto' },
  { value: 'America/Halifax', label: 'Atlantic — Halifax' },
  { value: 'America/St_Johns', label: 'Newfoundland — St. John\'s' },
  { value: 'Asia/Kolkata', label: 'India — IST' },
  { value: 'Europe/London', label: 'UK — London' },
  { value: 'UTC', label: 'UTC' },
];
const KEY = 'cmx_admin_tz';
const EVT = 'cmx:tz';

export function getAdminTz() {
  try {
    const v = localStorage.getItem(KEY);
    return v && TIMEZONES.some((t) => t.value === v) ? v : DEFAULT_TZ;
  } catch (_) { return DEFAULT_TZ; }
}

export function setAdminTz(tz) {
  try { localStorage.setItem(KEY, tz); } catch (_) { /* ignore */ }
  window.dispatchEvent(new CustomEvent(EVT));
}

export function useAdminTz() {
  const [tz, setTz] = useState(getAdminTz);
  useEffect(() => {
    const h = () => setTz(getAdminTz());
    window.addEventListener(EVT, h);
    return () => window.removeEventListener(EVT, h);
  }, []);
  return [tz, setAdminTz];
}

const toDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const format = (v, opts) => {
  const d = toDate(v);
  if (!d) return '—';
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: getAdminTz(), ...opts }).format(d); } catch (_) { return d.toLocaleString(); }
};

export const fmtDate = (v) => format(v, { year: 'numeric', month: 'short', day: 'numeric' });
export const fmtDateShort = (v) => format(v, { month: 'short', day: 'numeric' });
export const fmtDateTime = (v) => format(v, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
export const fmtDateTimeShort = (v) => format(v, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
export const fmtTime = (v) => format(v, { hour: 'numeric', minute: '2-digit' });

export function tzAbbr(tz = getAdminTz(), at = new Date()) {
  try {
    const part = new Intl.DateTimeFormat('en-CA', { timeZone: tz, timeZoneName: 'short' }).formatToParts(at).find((p) => p.type === 'timeZoneName');
    return part ? part.value : tz;
  } catch (_) { return tz; }
}

/* Today's calendar date (YYYY-MM-DD) in the admin timezone — for date pickers. */
export function todayInTz(tz = getAdminTz(), offsetDays = 0) {
  const d = new Date(Date.now() + offsetDays * 86400000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}
